import { isBooleanAlgebraOperation } from '@/adapters/primitive/_pure/derivation/boolean/algebra';
import { generatePrimitiveTrace } from '@/adapters/primitive/_pure/model/trace';
import type { PrimitiveOperationInput } from '@/adapters/primitive/_contract/operation';
import type { PrimitiveTrace, PrimitiveTraceSettings } from '@/adapters/primitive/_contract/trace';

function record(value: unknown): Record<string, unknown> {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) throw new Error('Primitive trace requires an object.');
    return value as Record<string, unknown>;
}

function text(value: unknown): string {
    if (typeof value !== 'string' || !value.trim()) throw new Error('Primitive trace requires nonempty identifiers.');
    return value;
}

function natural(value: unknown, maximum: number): number {
    if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0 || value > maximum) {
        throw new Error(`Primitive trace operand must be an integer from 0 to ${maximum}.`);
    }
    return value;
}

function boolean(value: unknown): boolean {
    if (typeof value !== 'boolean') throw new Error('Primitive trace requires Boolean operands and settings.');
    return value;
}

function parseInput(value: unknown): PrimitiveOperationInput {
    const input = record(value);
    const operation = text(input.operation);
    switch (operation) {
        case 'addition':
        case 'subtraction':
        case 'multiplication':
        case 'division':
            return { operation, operandOne: natural(input.operandOne, operation === 'addition' || operation === 'subtraction' ? 999 : 99),
                operandTwo: natural(input.operandTwo, operation === 'addition' || operation === 'subtraction' ? 999 : 99) };
        case 'natural-less-than':
            return { operation, source: natural(input.source, 999), target: natural(input.target, 999) };
        case 'natural-identity':
            return { operation, input: natural(input.input, 999) };
        case 'boolean-identity':
        case 'logical-not':
            return { operation, input: boolean(input.input) };
        default:
            if (!isBooleanAlgebraOperation(operation) || operation === 'logical-not') throw new Error(`Unsupported primitive trace operation '${operation}'.`);
            return { operation, operandOne: boolean(input.operandOne), operandTwo: boolean(input.operandTwo) };
    }
}

function parseSettings(value: unknown): PrimitiveTraceSettings {
    const settings = record(value);
    if (settings.radix !== 10) throw new Error('Primitive traces currently support radix 10 only.');
    if (settings.operandSchedulingMode !== 'interleaved' && settings.operandSchedulingMode !== 'grouped') {
        throw new Error('Invalid primitive trace operand scheduling mode.');
    }
    const budget = (value: unknown) => {
        const parsed = natural(value, Number.MAX_SAFE_INTEGER);
        if (parsed === 0) throw new Error('Primitive trace runaway budgets must be positive.');
        return parsed;
    };
    return { radix: 10, operandSchedulingMode: settings.operandSchedulingMode,
        allowExoticTerminalStates: boolean(settings.allowExoticTerminalStates),
        runawayBorrowAssemblyBudget: budget(settings.runawayBorrowAssemblyBudget),
        runawayDivisionCycleBudget: budget(settings.runawayDivisionCycleBudget) };
}

// Version 1 validates every persisted state/event against the deterministic semantic model, once at catalog load.
function matchesCanonical<T>(value: unknown, canonical: T): value is T {
    if (canonical === null || typeof canonical !== 'object') return Object.is(value, canonical);
    if (Array.isArray(canonical)) {
        return Array.isArray(value) && value.length === canonical.length
            && canonical.every((entry, index) => matchesCanonical(value[index], entry));
    }
    if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
    const expected = Object.entries(canonical).filter(([, entry]) => entry !== undefined);
    const actual = record(value);
    return Object.keys(actual).length === expected.length
        && expected.every(([key, entry]) => Object.hasOwn(actual, key) && matchesCanonical(actual[key], entry));
}

export function parsePrimitiveTrace(value: unknown): PrimitiveTrace {
    const trace = record(value);
    if (trace.adapter !== 'primitive-space' || trace.traceKind !== 'PrimitiveTrace' || trace.schemaVersion !== 1) {
        throw new Error('Unsupported primitive trace reference or schema version.');
    }
    const payload = record(trace.payload);
    const canonical = generatePrimitiveTrace({
        handle: text(trace.handle), title: text(trace.title), input: parseInput(payload.input),
        settings: parseSettings(payload.settings),
    });
    if (!matchesCanonical(value, canonical)) throw new Error(`Primitive trace '${canonical.handle}' has invalid fields or semantic data.`);
    return value;
}

export function parsePrimitiveTraceCatalog(values: readonly unknown[]): readonly PrimitiveTrace[] {
    const traces = values.map(parsePrimitiveTrace);
    if (new Set(traces.map((trace) => trace.handle)).size !== traces.length) throw new Error('Duplicate primitive trace handles.');
    return traces;
}
